import * as Sentry from '@sentry/browser';
import { useCreateAdminToken, useReadAdminUserProfile } from 'lib/api/admin/hooks';
import { CreateAdminTokenResponseBody, ReadAdminUserProfileResponse } from 'lib/api/admin/types';
import { useAuthLS } from 'lib/hooks/useAuthLS';
import { ReactNode, useEffect, useState } from 'react';
import { AuthContext, AuthContextValue, AuthState } from '../context/AuthContext';

// 5/29 MTG: superadmin / general / signal の 3 ロールが signal アプリにログイン可能
const LOGIN_GRANTED_ROLE = ['superadmin', 'general', 'signal'];

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { setToken, getToken, removeToken } = useAuthLS();
  const accessToken = getToken('accessToken');
  const { trigger: requestToken, isLoading: isTokenRequestLoading } = useCreateAdminToken();
  const { trigger: getUserProfile } = useReadAdminUserProfile();

  const [authState, setAuthState] = useState<AuthState>(accessToken ? 'IDLE' : 'UNAUTHENTICATED');
  const [profile, setProfile] = useState<AuthContextValue['profile']>(undefined);

  function determineAuthState(response: ReadAdminUserProfileResponse | undefined) {
    if (!response) {
      setAuthState('UNAUTHENTICATED');
      return;
    }
    const { role, isAvailable } = response.adminUser;
    if (isAvailable && LOGIN_GRANTED_ROLE.includes(role)) {
      setAuthState('AUTHENTICATED');
    } else {
      setAuthState('UNAUTHENTICATED');
    }
  }

  function handleLoginResponse(response: CreateAdminTokenResponseBody | undefined) {
    if (!response) throw new Error('ログインに失敗しました');
    setToken({ accessToken: response.access_token, refreshToken: response.refresh_token });
  }

  function handleUserProfile(response: ReadAdminUserProfileResponse | undefined) {
    if (!response) throw new Error('ユーザープロファイルの取得に失敗しました');
    const { id, username, email } = response.adminUser;
    Sentry.setUser({ id, username, email });
    setProfile(response.adminUser);
  }

  function logout() {
    removeToken('both');
    setAuthState('UNAUTHENTICATED');
    setProfile(undefined);
  }

  async function login({ username, password }: { username: string; password: string }) {
    try {
      setAuthState('IDLE');
      const tokenResponse = await requestToken({
        body: { username, password },
        config: { headers: { 'content-Type': 'application/x-www-form-urlencoded' } },
      });
      handleLoginResponse(tokenResponse);

      const profileResponse = await getUserProfile();
      handleUserProfile(profileResponse);
      determineAuthState(profileResponse);
    } catch (e) {
      console.error(e);
      logout();
      throw e;
    }
  }

  useEffect(() => {
    if (!accessToken) return;
    (async () => {
      try {
        const response = await getUserProfile();
        handleUserProfile(response);
        determineAuthState(response);
      } catch (e) {
        console.error(e);
        logout();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const value: AuthContextValue = {
    authState,
    accessToken,
    profile,
    login,
    logout,
    isLoading: isTokenRequestLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
